/**
 * @zendir/ui - Box Layout Primitive
 *
 * The foundational layout building block. A themed <div> with shorthand props
 * for padding, margin, background, border, and display properties.
 *
 * Follows Astro UX spacing tokens (4px grid system).
 * All spacing props accept SpacingTokens ('xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'),
 * numbers (px), or CSS strings.
 *
 * @example
 * ```tsx
 * <Box padding="md" background="surface">
 *   <H3>Section Title</H3>
 *   <Body1>Content here</Body1>
 * </Box>
 *
 * <Box padding={['sm', 'md', 'lg']} borderRadius="md">
 *   Responsive padding
 * </Box>
 * ```
 */

import React, { memo, forwardRef } from "react";
import { useTheme } from "../../theme";
import { useBreakpoint } from "./useBreakpoint";
import { resolveResponsiveSpacing, resolveResponsive } from "./responsive";
import type { ResponsiveValue } from "./useBreakpoint";
import type { SpacingToken } from "./responsive";

type SpacingInput = SpacingToken | string | number;

export interface BoxProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Element type to render */
  as?: React.ElementType;

  // Spacing
  /** Padding (all sides) */
  padding?: ResponsiveValue<SpacingInput>;
  /** Shorthand alias for padding */
  p?: ResponsiveValue<SpacingInput>;
  /** Padding horizontal (left + right) */
  px?: ResponsiveValue<SpacingInput>;
  /** Padding vertical (top + bottom) */
  py?: ResponsiveValue<SpacingInput>;
  /** Padding top */
  pt?: ResponsiveValue<SpacingInput>;
  /** Padding right */
  pr?: ResponsiveValue<SpacingInput>;
  /** Padding bottom */
  pb?: ResponsiveValue<SpacingInput>;
  /** Padding left */
  pl?: ResponsiveValue<SpacingInput>;
  /** Margin (all sides) */
  margin?: ResponsiveValue<SpacingInput>;
  /** Shorthand alias for margin */
  m?: ResponsiveValue<SpacingInput>;
  /** Margin horizontal (left + right) */
  mx?: ResponsiveValue<SpacingInput>;
  /** Margin vertical (top + bottom) */
  my?: ResponsiveValue<SpacingInput>;
  /** Margin top */
  mt?: ResponsiveValue<SpacingInput>;
  /** Margin right */
  mr?: ResponsiveValue<SpacingInput>;
  /** Margin bottom */
  mb?: ResponsiveValue<SpacingInput>;
  /** Margin left */
  ml?: ResponsiveValue<SpacingInput>;

  // Visual
  /** Background color: 'base' | 'surface' | 'elevated' | 'overlay' | custom CSS color */
  background?: string;
  /** Border: true for default border, or custom CSS border */
  border?: boolean | string;
  /** Border radius token or CSS value */
  borderRadius?: "none" | "sm" | "md" | "lg" | "xl" | "full" | string;
  /** Box shadow token or CSS value */
  shadow?: "none" | "sm" | "md" | "lg" | "xl" | string;

  // Layout
  /** CSS display property */
  display?: ResponsiveValue<string>;
  /** CSS width */
  width?: ResponsiveValue<string | number>;
  /** CSS height */
  height?: ResponsiveValue<string | number>;
  /** CSS min-width */
  minWidth?: string | number;
  /** CSS min-height */
  minHeight?: string | number;
  /** CSS max-width */
  maxWidth?: string | number;
  /** CSS max-height */
  maxHeight?: string | number;
  /** CSS overflow */
  overflow?: string;
  /** CSS position */
  position?: "static" | "relative" | "absolute" | "fixed" | "sticky";
  /** CSS flex property */
  flex?: string | number;
  /** CSS flex-grow */
  flexGrow?: number;
  /** CSS flex-shrink */
  flexShrink?: number;

  /** Children */
  children?: React.ReactNode;
}

export const Box = memo(
  forwardRef<HTMLDivElement, BoxProps>(function Box(
    {
      as: Component = "div",
      padding,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      margin,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      background,
      border,
      borderRadius,
      shadow,
      display,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      overflow,
      position,
      flex,
      flexGrow,
      flexShrink,
      style,
      children,
      ...rest
    },
    ref,
  ): React.ReactElement {
    const { tokens } = useTheme();
    const { breakpoint } = useBreakpoint();

    const resolve = (val: ResponsiveValue<SpacingInput> | undefined) =>
      resolveResponsiveSpacing(val, breakpoint, tokens);

    // Resolve padding (p is alias for padding)
    const paddingAll = resolve(padding ?? p);
    const paddingX = resolve(px);
    const paddingY = resolve(py);

    // Resolve margin (m is alias for margin)
    const marginAll = resolve(margin ?? m);
    const marginX = resolve(mx);
    const marginY = resolve(my);

    // Background color resolution
    const resolveBackground = (bg: string | undefined): string | undefined => {
      if (!bg) return undefined;
      const bgMap: Record<string, string> = {
        base: tokens.colors.background.base,
        surface: tokens.colors.background.surface,
        elevated: tokens.colors.background.elevated,
        overlay: tokens.colors.background.overlay,
      };
      return bgMap[bg] || bg;
    };

    // Border resolution
    const resolveBorder = (
      b: boolean | string | undefined,
    ): string | undefined => {
      if (!b) return undefined;
      if (b === true) return `1px solid ${tokens.colors.border.default}`;
      return b;
    };

    // Border radius resolution
    const resolveBorderRadius = (
      br: string | undefined,
    ): string | undefined => {
      if (!br) return undefined;
      if (br in tokens.borderRadius)
        return tokens.borderRadius[br as keyof typeof tokens.borderRadius];
      return br;
    };

    // Shadow resolution
    const resolveShadow = (s: string | undefined): string | undefined => {
      if (!s) return undefined;
      if (s in tokens.shadows && s !== "glow")
        return tokens.shadows[s as keyof Omit<typeof tokens.shadows, "glow">];
      return s;
    };

    // Size resolution (number → px)
    const resolveSize = (
      val: string | number | undefined,
    ): string | undefined => {
      if (val === undefined) return undefined;
      if (typeof val === "number") return `${val}px`;
      return val;
    };

    const computedStyle: React.CSSProperties = {
      // Padding
      ...(paddingAll ? { padding: paddingAll } : {}),
      ...(paddingX ? { paddingLeft: paddingX, paddingRight: paddingX } : {}),
      ...(paddingY ? { paddingTop: paddingY, paddingBottom: paddingY } : {}),
      ...(resolve(pt) ? { paddingTop: resolve(pt) } : {}),
      ...(resolve(pr) ? { paddingRight: resolve(pr) } : {}),
      ...(resolve(pb) ? { paddingBottom: resolve(pb) } : {}),
      ...(resolve(pl) ? { paddingLeft: resolve(pl) } : {}),

      // Margin
      ...(marginAll ? { margin: marginAll } : {}),
      ...(marginX ? { marginLeft: marginX, marginRight: marginX } : {}),
      ...(marginY ? { marginTop: marginY, marginBottom: marginY } : {}),
      ...(resolve(mt) ? { marginTop: resolve(mt) } : {}),
      ...(resolve(mr) ? { marginRight: resolve(mr) } : {}),
      ...(resolve(mb) ? { marginBottom: resolve(mb) } : {}),
      ...(resolve(ml) ? { marginLeft: resolve(ml) } : {}),

      // Visual
      backgroundColor: resolveBackground(background),
      border: resolveBorder(border),
      borderRadius: resolveBorderRadius(borderRadius),
      boxShadow: resolveShadow(shadow),

      // Layout
      display: resolveResponsive(display, breakpoint),
      width: resolveSize(resolveResponsive(width, breakpoint)),
      height: resolveSize(resolveResponsive(height, breakpoint)),
      minWidth: resolveSize(minWidth),
      minHeight: resolveSize(minHeight),
      maxWidth: resolveSize(maxWidth),
      maxHeight: resolveSize(maxHeight),
      overflow,
      position,
      flex,
      flexGrow,
      flexShrink,

      // User overrides
      ...style,
    };

    return (
      <Component ref={ref} style={computedStyle} {...rest}>
        {children}
      </Component>
    );
  }),
);

export default Box;
